import { ProjectCard } from '@/components/home-v2/Projects/user-and-community-projects'
import { ProjectListSkeleton } from '@/components/project-skeleton'
import { Button } from '@/components/ui/button'
import type { UserProject } from '@/lib/types'
import { cn, fetcher } from '@/lib/utils'
import { useTranslations } from 'next-intl'
import useSWRInfinite from 'swr/infinite'

interface IProjectList {
  currentProjectId?: string
  className?: string
  maxColumns?: number
  showSkeletonHeader?: boolean
  linkToNewTab?: boolean
}

export const ProjectList = ({
  currentProjectId,
  className,
  maxColumns = 3,
  showSkeletonHeader = true,
  linkToNewTab = true,
}: IProjectList) => {
  const t = useTranslations('project-preview')

  const limit = 20
  const { data, error, size, setSize, isLoading, isValidating, mutate } =
    useSWRInfinite((pageIndex: number) => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (pageIndex * limit).toString(),
      })
      return `/api/me/projects?${params}`
    }, fetcher)

  const projects: UserProject[] = data ? data.flat() : []
  const isReachingEnd = data && data[data.length - 1]?.length < limit

  if (isLoading && projects.length === 0)
    return (
      <ProjectListSkeleton
        maxColumns={maxColumns}
        showSkeletonHeader={showSkeletonHeader}
      />
    )
  if (error) return <p className="text-red-500">{t('failed')}</p>
  if (!projects || projects.length === 0) return null

  return (
    <>
      <div
        x-test-id="project-list"
        className={cn(
          'grid grid-cols-1 md:grid-cols-1 lg:grid-cols-1 gap-4',
          className,
        )}
      >
        {projects
          .filter((project) => project._id.toString() !== currentProjectId)
          .map((project) => (
            <ProjectCard
              key={project._id.toString()}
              project={project}
              linkToNewTab={linkToNewTab}
              isOwner={true}
              showActions
              cardClassName="border-none hover:shadow-md"
              showAvatar={false}
              mutate={mutate}
            />
          ))}
      </div>

      <div className="flex justify-center mt-6">
        {!isReachingEnd && (
          <Button onClick={() => setSize(size + 1)} disabled={isValidating}>
            {isValidating ? t('loading') : t('load_more')}
          </Button>
        )}
        {isReachingEnd && projects.length > 0 && (
          <p className="text-gray-300">{t('no_more')}</p>
        )}
      </div>
      <div className="h-8" />
    </>
  )
}
